import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { SelectedCenterCardComponent } from '@components/selected-center-card/selected-center-card.component';
import { Sites } from '@enums/sites';
import { StorageKeys } from '@enums/storage-keys';
import { environment } from '@environments/environment';
import {
  InHomeBookingResult,
  InHomeCollectionService as FindalabIHCService,
  Map,
  NgFindalabModule,
  Result,
  ResultService,
} from '@Medology/ng-findalab';
import { Center } from '@models/center';
import { Test } from '@models/test';
import { FormService } from '@services/form.service';
import { InHomeCollectionService } from '@services/in-home-collection.service';
import { LoadingService } from '@services/loading.service';
import { MapsLoadService } from '@services/maps-load.service';
import { PscService } from '@services/psc.service';
import { StorageService } from '@services/storage.service';
import { Observable, of, Subscription } from 'rxjs';

@Component({
  selector: 'app-test-center',
  templateUrl: './test-center.component.html',
  styleUrls: ['./test-center.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgClass, CardComponent, SelectedCenterCardComponent, NgFindalabModule],
})
export class TestCenterComponent implements OnInit {
  @Input() public apiUrl: string;
  @Input() public showHeader: boolean = true;
  @Input() public showSelectedCenter: boolean = true;
  @Input() public inHomeCollection: boolean = true;
  @Input() public zipCode: string;
  @Input() public centerData: Center;
  @Input() public noSelectedCenterLabel = 'No location has been set.';
  @Input() public tests: Test[] = [];

  @Output() public inHomeCollectionStatusChange: EventEmitter<void> = new EventEmitter<void>();

  public allButtonsDisabled: boolean = false;
  public map: Map = null;
  public appIsLoading: boolean = false;
  public pscError: string | null = null;
  public inHomeScheduling: boolean = false;

  private resultService = inject(ResultService);
  private inHomeService = inject(FindalabIHCService);
  private storage = inject(StorageService);
  private formService = inject(FormService);
  private mapsLoadService = inject(MapsLoadService);
  private loadingService = inject(LoadingService);
  private pscService = inject(PscService);
  private ihcService = inject(InHomeCollectionService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly invalidLabError =
    'Sorry, unfortunately, the lab you chose cannot provide all of the tests in your cart. Please choose a new lab below.';
  private readonly invalidIHCProviderError =
    'Sorry, unfortunately, we cannot conduct an in-home collection at your chosen appointment time for some of the tests in your cart. Please try again or select a nearby lab to visit.';

  private changingLab: boolean = false;
  private previouslySelectedLab: Result = null;

  /**
   * Gets the selected center label set on environment.
   */
  public get selectedCenterLabel(): string {
    return environment.titles.selectedCenterLabel;
  }

  /**
   * Gets the stored lab.
   */
  public get lab(): Result | null {
    const labProperties = this.storage.center;

    return labProperties ? new Result(labProperties) : null;
  }

  /**
   * Gets the In Home Booking Result.
   */
  public get inHomeResult(): InHomeBookingResult | null {
    return this.storage.inHomeBookingResult;
  }

  /**
   * Determines if the map should be displayed or not.
   */
  public get shouldDisplayMap(): boolean {
    return (
      ((!this.hasLab && !this.hasInHomeBookingResult) || this.changingLab || this.hasBetterLab) && !this.appIsLoading
    );
  }

  /**
   * Determines the title of the first step.
   */
  public get step1Title(): string {
    return this.hasInHomeBookingResult || this.inHomeScheduling
      ? 'In-Home Collection Address'
      : `${environment.titles.step1Title}`;
  }

  /**
   * Determine if in home booking result exists.
   */
  private get hasInHomeBookingResult(): boolean {
    return !!this.storage.inHomeBookingResult;
  }

  /**
   * Determines whether a lab is stored already or not.
   */
  private get hasLab(): boolean {
    return !!this.lab;
  }

  /**
   * Determine if better lab exists.
   */
  private get hasBetterLab(): boolean {
    return !!this.storage.betterLab;
  }

  @Input() public set reEnableButtons(_reEnableButtons: boolean) {
    if (_reEnableButtons) {
      this.allButtonsDisabled = false;
    }
  }

  /**
   * Configures the map.
   */
  public ngOnInit(): void {
    this.loadListeners();
    this.preselectCenter();
    this.setTestingLocationResult();
  }

  /**
   * Loads the map.
   */
  public loadMap(): void {
    this.mapsLoadService.getGoogleMapApi(environment.googleMapApiKey).subscribe(() => {
      this.getNetworksFromTestsAsObservable().subscribe((networks) => {
        if (this.storage.center) {
          this.validateSelectedLabNetwork(networks);
        }

        if (this.storage.inHomeBookingResult) {
          this.validateInHomeCollectionProviderNetwork(networks);
        }

        this.map = new Map(this.getMapConfig(this.zipCode || this.resultService.getPostalCode(), networks));
      });
    });
  }

  /**
   * Returns an observable that gets the networks for the map configuration.
   */
  public getNetworksFromTestsAsObservable(): Observable<string[]> {
    if (!environment.ngFindalab.getNetworksFromTests) {
      return of([]);
    }

    return this.pscService.getTestNetworks(this.tests);
  }

  /**
   * Method to trigger the center change.
   */
  public changeLab(): void {
    if (this.storage.zipcode) {
      this.map.postalCode = this.storage.zipcode;
    }

    if (this.storage.inHomeBookingResult) {
      this.inHomeCollectionStatusChange.emit();
    }

    this.storage.remove(StorageKeys.Fee);
    this.storage.remove(StorageKeys.InHomeBookingResult);
    this.changingLab = !this.changingLab;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Gets map config object.
   */
  public getMapConfig(zipcode: string, networks: string[] = []): Map {
    let map = {
      searchUrl: {
        hostname: `${environment.analyteCareApi}/api/v1`,
        path: environment.ngFindalab.searchEndpoint,
      },
      inHomeServiceAreaUrl: {
        hostname: `${environment.analyteCareApi}/api/v1`,
        path: '/in-home/service-area',
      },
      inHomeAvailabilityUrl: {
        hostname: `${environment.analyteCareApi}/api/v1`,
        path: '/in-home/availability',
      },
      apiKey: environment.googleMapApiKey,
      testing: !environment.production,
      searchFunction: {
        onlyNetworks: networks,
        onlyStates: environment.ngFindalab.filterByStates,
        filterByOrder: '',
        filterByTests: null,
        filterBySite: '',
      },
      inputGroup: {
        container: environment.ngFindalab.inputGroup.container,
        field: environment.ngFindalab.inputGroup.field,
        button: environment.ngFindalab.inputGroup.button,
      },
      lab: {
        phone: {
          show: environment.ngFindalab.showPhone,
          text: 'Phone:',
        },
        button: {
          show: true,
          text: environment.ngFindalab.labButtonText,
          class: environment.styles.testCenter.labButtonClass,
        },
        icon: {
          show: true,
          class: environment.ngFindalab.labIconClass,
        },
        recommended: {},
      },
      search: {
        title: environment.ngFindalab.searchTitle,
        description: environment.ngFindalab.searchDescription,
        button: {
          class: `button ${environment.styles.testCenter.searchButtonClass}`,
          loadingText: '',
          text: environment.ngFindalab.searchButtonText,
          icon: {
            show: environment.ngFindalab.showSearchIcon,
            class: 'fa fa-search',
            loadingClass: 'fa fa-spinner fa-spin',
          },
        },
        placeholder: 'Enter your zip',
        inputType: 'tel',
        showDescription: true,
        messageNoResults: environment.ngFindalab.searchMessageNoResults,
      },
      userLocation: {
        showOption: true,
        icon: 'fa fa-map-marker',
        msg: environment.ngFindalab.locateMsg,
        loading: {
          icon: 'fa fa-spin fa-spinner',
          msg: 'Searching current location...',
        },
      },
      dayOfWeekFilter: {
        showOption: environment.ngFindalab.showDayOfWeekFilter,
        radioAllText: 'All',
        radioDaysText: {
          0: null,
          1: null,
          2: null,
          3: null,
          4: null,
          5: null,
          6: 'Open Saturdays',
        },
      },
      postalCode: zipcode ?? null,
      mobileBreakpoint: 848,
      googleMaps: {
        defaultLat: 39.97712,
        defaultLong: -99.587403,
        geoCoder: null,
        infoWindow: null,
        initialZoom: 4,
        map: null,
        markers: [],
        resultsZoom: environment.ngFindalab.resultsZoom,
        labMarkerFillColor: environment.styles.testCenter.googleMapsLabMarkerFillColor,
        recommendedMarkerFillColor: environment.styles.testCenter.googleMapsRecommendedMarkerFillColor,
        markerHoverFillColor: environment.styles.testCenter.googleMapsMarkerHoverFillColor,
      },
      inHomeCollection: {
        enabled: this.inHomeCollection,
        addressPageTitle: 'Where would you like us to send a nurse for your sample collection?',
        addressPageDescription: '',
        onlyProviderIds: this.storage.providerIds,
        price: environment.ngFindalab.inHomeCollectionCostInCents,
      },
      mapId: environment.ngFindalab.findalabMapId,
    };

    if (environment.ngFindalab.filterByTestAndSite && !this.hasBetterLab) {
      map.searchFunction.filterByTests = { key: 'id', values: this.storage.tests.map((test) => test.id) };
      map.searchFunction.filterBySite = environment.siteId;
    }

    if (this.hasBetterLab) {
      map.searchFunction.filterByOrder = this.storage.transactionId;
      map.lab.recommended = {
        show: true,
        class: '',
        text: 'highly recommended',
      };
    }

    return map as Map;
  }

  /**
   *  Loads the map when it's preselected and also set the center data when it is not a HealthLabs domain.
   */
  private preselectCenter(): void {
    if (!this.centerData) {
      return;
    }

    if (this.centerData && (environment.site as Sites) !== Sites.Healthlabs) {
      this.storage.center = this.centerData;
    }

    this.loadMap();
  }

  /**
   * Method that loads all listeners.
   */
  private loadListeners(): void {
    this.listenForLoadingStatusChanges();
    this.listenForTestsSubjectChanges();
    this.listenForResultSelection();
    this.listenForLabIdStatusChanges();
    this.listenForBookingSubmission();
    this.listenForInHomeCollectionSchedule();
  }

  /**
   * Get a Subscription to the loading status changes.
   *
   * @returns a Subscription to loading$ Observable.
   */
  private listenForLoadingStatusChanges(): Subscription {
    return this.loadingService.loading$.subscribe((loadingStatus) => (this.appIsLoading = loadingStatus));
  }

  /**
   * Get a Subscription to tests subject changes.
   *
   * @returns a Subscription to storage.testsSubject Observable.
   */
  private listenForTestsSubjectChanges(): Subscription {
    if ((environment.site as Sites) === Sites.Treatmyuti || this.storage.betterLab) {
      return of([]).subscribe(() => this.loadMap());
    }

    return this.storage.testsSubject.subscribe(() => this.loadMap());
  }

  /**
   * Get a Subscription to the result selection event.
   *
   * @returns a Subscription to resultSelected$ Observable.
   */
  private listenForResultSelection(): Subscription {
    return this.resultService.resultSelected$.subscribe((result: Result) => {
      this.setLabResult(result);
      this.changingLab = false;
      this.pscError = null;
    });
  }

  /**
   * Get a Subscription to the lab id status changes.
   *
   * @returns a Subscription to lab_id checkout form observable.
   */
  private listenForLabIdStatusChanges(): Subscription {
    return this.formService.checkout.get('lab_id').statusChanges.subscribe(() => this.verifyTestsCanBeOrdered());
  }

  /**
   * Get a Subscription to the booking submission event.
   *
   * @returns a Subscription to bookingSubmitted$ Observable.
   */
  private listenForBookingSubmission(): Subscription {
    return this.inHomeService.bookingSubmitted$.subscribe((result: InHomeBookingResult) => {
      this.setInHomeBookingResult(result);
      this.inHomeScheduling = false;
      this.changingLab = false;
    });
  }

  /**
   * Get a Subscription to the scheduling event.
   *
   * @returns a Subscription to scheduling$ Observable.
   */
  private listenForInHomeCollectionSchedule(): Subscription {
    return this.inHomeService.scheduling$.subscribe((status: boolean) => {
      this.inHomeScheduling = status;
      if (this.inHomeScheduling) {
        this.previouslySelectedLab = this.storage.center;
        this.setLabValue(null);
      } else {
        this.setLabValue(this.previouslySelectedLab);
      }
    });
  }

  /**
   * Sets a PSC error variable when the selected lab can not provide the selected HealthLab's tests.
   */
  private verifyTestsCanBeOrdered(): void {
    if (!this.hasLab || !environment.ngFindalab.verifyTestsCanBeOrdered) {
      return;
    }

    this.pscService
      .verifyTestsAreAvailableAtLab(
        this.lab.id,
        this.tests.map((test) => test.id),
      )
      .subscribe((pscs: Result[]) => {
        if (!pscs.map((psc) => psc.id).includes(this.lab?.id)) {
          this.pscError = this.invalidLabError;
          this.changingLab = true;
        }
      });
  }

  /**
   * Set the result control validators based if is an in-home collection or not.
   *
   * @param {boolean} isInHomeCollection The expected control to have the result validator.
   */
  private setResultControlValidators(isInHomeCollection: boolean): void {
    const labControl = this.formService.checkout.get('lab_id');
    const bookingControl = this.formService.checkout.get('booking_id');

    this.formService.resetControl(labControl);
    this.formService.resetControl(bookingControl);
    this.formService.setValidator(isInHomeCollection ? bookingControl : labControl, [Validators.required]);
  }

  /**
   * Sets the lab value on LabControl and local storage.
   *
   * @param {Result | null} lab The lab to be set, or null to remove the lab.
   */
  private setLabValue(lab: Result | null): void {
    if (lab === null) {
      this.storage.remove(StorageKeys.Center);
    } else {
      this.storage.center = lab;
    }
    this.formService.checkout.get('lab_id').setValue(lab?.id ?? null);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Sets the lab value on LabControl and local storage.
   *
   * @param {InHomeBookingResult | null} inHomeBookingResult The in-home booking result to be set, or null to remove it.
   */
  private setInHomeBookingValue(inHomeBookingResult: InHomeBookingResult | null): void {
    if (inHomeBookingResult === null) {
      this.storage.remove(StorageKeys.InHomeBookingResult);
    } else {
      this.storage.inHomeBookingResult = inHomeBookingResult;
      this.inHomeCollectionStatusChange.emit();
    }
    this.formService.checkout.get('booking_id').setValue(inHomeBookingResult?.bookingSlot?.key ?? null);
  }

  /**
   * Sets the lab result.
   *
   * @param {Result} lab The lab.
   */
  private setLabResult(lab: Result): void {
    this.setResultControlValidators(false);
    this.setLabValue(lab);
    this.setInHomeBookingValue(null);
  }

  /**
   * Sets in home booking result.
   *
   * @param {InHomeBookingResult} inHomeBookingResult The in home booking result.
   */
  private setInHomeBookingResult(inHomeBookingResult: InHomeBookingResult): void {
    this.setResultControlValidators(true);
    this.setInHomeBookingValue(inHomeBookingResult);
    this.setLabValue(null);
  }

  /**
   * load the lab or in home booking result.
   */
  private setTestingLocationResult(): void {
    if (this.storage.inHomeBookingResult) {
      this.setInHomeBookingResult(this.storage.inHomeBookingResult);
    } else if (this.storage.center) {
      this.setLabResult(this.storage.center);
    }
  }

  /**
   * Validates the selected lab network.
   *
   * @param {string[]} networks the networks that can support the selected tests
   */
  private validateSelectedLabNetwork(networks: string[]): void {
    if (!environment.ngFindalab.validateSelectedLabNetwork || networks.includes(this.storage.center?.network?.name)) {
      return;
    }

    this.pscError = this.invalidLabError;
    this.setLabValue(null);
  }

  /**
   * Validates if the In-Home Collection (IHC) provider's networks include any of the specified networks.
   *
   * @param {string[]} networks the list of networks to validate against the provider's networks
   */
  private validateInHomeCollectionProviderNetwork(networks: string[]): void {
    if (!environment.ngFindalab.getNetworksFromTests) {
      return;
    }

    this.ihcService
      .getIHCProviderAssociatedNetworks(this.storage.inHomeBookingResult.bookingSlot.providerId)
      .subscribe((providerNetworks) => {
        if (providerNetworks.map((network) => network.name).some((network) => networks.includes(network))) {
          return;
        }

        this.pscError = this.invalidIHCProviderError;
        this.setLabResult(null);
      });
  }
}
